import React, { useEffect } from 'react';
import { Support } from '../../hooks/Support';
import Hls from 'hls.js';

export type FullScreenProps = {
  enabled: boolean;
  hls: Hls;
  code: string;
  supported: Support;
};

declare global {
  interface Document {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
    webkitCancelFullScreen?: () => Promise<void>;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
  }

  interface HTMLElement {
    msRequestFullscreen?: () => Promise<void>;
    mozRequestFullscreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
  }

  interface HTMLVideoElement {
    webkitEnterFullscreen: () => Promise<void>;
  }
}

const FullScreen = ({ enabled, hls, code, supported }: FullScreenProps) => {
  const [isFullScreen, setIsFullscreen] = React.useState<boolean>(false);

  const fullScreenVideo = () => {
    if (supported.isIOS) {
      const video = document.querySelector(`[data-code="${code}"] video`) as HTMLVideoElement;
      video.webkitEnterFullscreen();
      return;
    }

    if (document.webkitFullscreenElement && document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
      return;
    } else if (document?.documentElement?.webkitRequestFullscreen) {
      document.documentElement.webkitRequestFullscreen();
      return;
    }

    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }

    setIsFullscreen(!isFullScreen);
  };

  const onFullScreenChange = React.useCallback(() => {
    const isFullscreen = document.fullscreenElement
                      || document.webkitFullscreenElement
                      || document.mozFullScreenElement
                      || document.msFullscreenElement;

    if (isFullscreen) {
      hls?.media?.dispatchEvent(new CustomEvent('enterFullscreen', { detail: { fullScreen: true } }));
    } else {
      hls?.media?.dispatchEvent(new CustomEvent('leaveFullscreen', { detail: { fullScreen: false } }));
    }

    hls?.media?.dispatchEvent(new CustomEvent('changeFullscreen', { detail: { fullScreen: isFullScreen } }));
  }, [isFullScreen, hls]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFullScreenChange);
    document.addEventListener('webkitfullscreenchange', onFullScreenChange);
    document.addEventListener('mozfullscreenchange', onFullScreenChange);
    document.addEventListener('MSFullscreenChange', onFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullScreenChange);
      document.removeEventListener('mozfullscreenchange', onFullScreenChange);
      document.removeEventListener('MSFullscreenChange', onFullScreenChange);
    };
  }, [onFullScreenChange]);

  return enabled ? (
    <div className={`smartplayer__fullscreen ${isFullScreen ? 'smartplayer__fullscreen--active' : 'smartplayer__fullscreen--inactive'}`} onClick={() => fullScreenVideo()}>
      <button className="smartplayer__bar-button" type="button" disabled={!supported.fullscreen && !supported.isIOS}>
        <div className="icon icon--open-fullscreen">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512" fill="currentColor">
            <path fill="currentColor" d="M32 64C14.3 64 0 78.3 0 96v96c0 17.7 14.3 32 32 32s32-14.3 32-32V128h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H32zM64 320c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H64V320zM384 64c-17.7 0-32 14.3-32 32s14.3 32 32 32h64v64c0 17.7 14.3 32 32 32s32-14.3 32-32V96c0-17.7-14.3-32-32-32H384zM512 320c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H384c-17.7 0-32 14.3-32 32s14.3 32 32 32h96c17.7 0 32-14.3 32-32V320z"></path>
          </svg>
        </div>
        <div className="icon icon--close-fullscreen">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512">
            <path fill="currentColor" d="M439 7c9.4-9.4 24.6-9.4 33.9 0l32 32c9.4 9.4 9.4 24.6 0 33.9l-87 87 39 39c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H296c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2l39 39L439 7zM72 272H216c13.3 0 24 10.7 24 24V440c0 9.7-5.8 18.5-14.8 22.2s-19.3 1.7-26.2-5.2l-39-39L73 505c-9.4 9.4-24.6 9.4-33.9 0L7 473c-9.4-9.4-9.4-24.6 0-33.9l87-87L55 313c-6.9-6.9-8.9-17.2-5.2-26.2s12.5-14.8 22.2-14.8z"></path>
          </svg>
        </div>
      </button>
    </div>
  ) : null;
};

export default FullScreen;
